// Copy of components/transactionSearchPage/TransactionSearchPage.state.js on its own Redux slice for the Payments - Beta tab.
import S from "sanctuary";
import { createSelector } from "reselect";
import {
  getSortedServiceTypes,
  getSortedPaymentSources,
  getSortedAgencies,
} from "@/components/currentUser/Agencies.state";
import {
  FILTERS,
  AGENCIES_FILTER,
  SERVICE_TYPE_FILTER,
  PAYMENT_SOURCE_FILTER,
} from "@/components/transactionSearchPage/FiltersForm";
import {
  changeCalendarDates,
  openDatesModal,
} from "@/components/datePickerV2Beta/DatePickerV2Beta.state";
import {
  getDefaultSearchDates,
  SEARCH_DATES,
} from "@/components/datePicker/Search.dates.util";
import {
  getTransactionSearchStartDate as getDatePickerStartDate,
  getTransactionSearchEndDate as getDatePickerEndDate,
} from "@/components/datePickerV2Beta/DatePickerV2Beta.selectors";
import { getUserTimezone } from "@/components/currentUser/CurrentUser.state";
import {
  FILTERS_VIEWS,
  FILTERS_VIEW_VALUES,
} from "@/components/transactionSearchPage/filterPresentation";
import {
  TRANSACTION_DATE_FILTER,
  FUNDED_TIMESTAMP_FILTER,
} from "@/components/transactionSearchPage/TransactionSearchPage.state";

export const CLEAR_FILTERS = "transactionSearchV2Beta/CLEAR_FILTERS";
export const clearFilters = () => ({
  type: CLEAR_FILTERS,
});

export const RESET_STATE = "transactionSearchV2Beta/RESET_STATE";
export const resetState = () => ({
  type: RESET_STATE,
});

export const TRANSACTION_SEARCH_PENDING =
  "transactionSearchV2Beta/TRANSACTION_SEARCH_PENDING";
export const transactionSearchPending = () => ({
  type: TRANSACTION_SEARCH_PENDING,
});

// Outcome actions name their search in `meta`, so `payload` stays the error
// envelope the alert reads.
export const SEARCH_REQUEST_FAILED = "transactionSearchV2Beta/SEARCH_REQUEST_FAILED";
export const searchRequestFailed = ({ error, searchId = null }) => ({
  type: SEARCH_REQUEST_FAILED,
  error: true,
  payload: error,
  meta: { searchId },
});

export const SEARCH_REQUEST_SUCCEEDED =
  "transactionSearchV2Beta/SEARCH_REQUEST_SUCCEEDED";
export const searchRequestSucceeded = ({ searchId = null } = {}) => ({
  type: SEARCH_REQUEST_SUCCEEDED,
  meta: { searchId },
});

// A caller that has to recognize its own response passes a `searchId`; the epic
// stamps one otherwise. Read through the payload, since some callers are wired
// straight to a DOM handler and arrive with an event.
export const REQUEST_SEARCH = "transactionSearchV2Beta/REQUEST_SEARCH";
export const requestSearch = (payload) => ({
  type: REQUEST_SEARCH,
  payload: { searchId: payload?.searchId ?? null },
});

export const REQUEST_PAGINATED_SEARCH =
  "transactionSearchV2Beta/REQUEST_PAGINATED_SEARCH";
export const requestPaginatedSearch = ({ page }) => ({
  type: REQUEST_PAGINATED_SEARCH,
  payload: { page },
});

// Natural-language translation (POST /transaction-search/translate).
export const TRANSLATE_REQUEST = "transactionSearchV2Beta/TRANSLATE_REQUEST";
export const translateRequest = ({ query }) => ({
  type: TRANSLATE_REQUEST,
  payload: { query },
});

export const TRANSLATE_SUCCEEDED = "transactionSearchV2Beta/TRANSLATE_SUCCEEDED";
export const translateSucceeded = ({ result }) => ({
  type: TRANSLATE_SUCCEEDED,
  payload: { result },
});

export const TRANSLATE_FAILED = "transactionSearchV2Beta/TRANSLATE_FAILED";
export const translateFailed = ({ error }) => ({
  type: TRANSLATE_FAILED,
  error: true,
  payload: error,
});

export const CLEAR_TRANSLATE = "transactionSearchV2Beta/CLEAR_TRANSLATE";
export const clearTranslate = () => ({
  type: CLEAR_TRANSLATE,
});

// Kept in the store because the page unmounts on tab navigation; a returning
// visit must not re-run the result's search.
export const TRANSLATE_RESULT_APPLIED =
  "transactionSearchV2Beta/TRANSLATE_RESULT_APPLIED";
export const markTranslateResultApplied = () => ({
  type: TRANSLATE_RESULT_APPLIED,
});

// Which filters the container shows. It lives in the
// reducer so it survives tab navigation, which unmounts the page.
export const SET_FILTERS_VIEW = "transactionSearchV2Beta/SET_FILTERS_VIEW";
export const setFiltersView = (view) => ({
  type: SET_FILTERS_VIEW,
  payload: { view },
});

// Shared with the classic slice; DatePicker and the request template compare
// against the classic constants.
export { TRANSACTION_DATE_FILTER, FUNDED_TIMESTAMP_FILTER };

export const OPEN_MODAL = "transactionSearchV2BetaUi/OPEN_MODAL";
export const openModal = ({ modalKey }) => ({
  type: OPEN_MODAL,
  payload: { modalKey },
});

export const CLOSE_MODAL = "transactionSearchV2BetaUi/CLOSE_MODAL";
export const closeModal = () => ({
  type: CLOSE_MODAL,
});

export const MODAL_KEYS = {
  DATES_MODAL: "dates_modal",
  SEARCH_MODAL: "search_modal",
};

export const NEXT_PENDING_TEXT_FRAME =
  "transactionSearchV2BetaUi/NEXT_PENDING_TEXT_FRAME";
export const nextPendingTextFrame = () => ({
  type: NEXT_PENDING_TEXT_FRAME,
});

export const nextPendingTextFrameMap = {
  Pending: "Pending.",
  "Pending.": "Pending..",
  "Pending..": "Pending...",
  "Pending...": "Pending",
};

const INITIAL_STATE = {
  modalCurrentlyOpen: null,
  searchPendingText: "Pending",
  filtersView: FILTERS_VIEWS.ALL,
  searchRequestPending: false,
  searchRequestError: S.Nothing,
  // The last search to come back: `{ searchId, failed }`, or null before any
  // has. The only signal for "did *my* search settle".
  lastSettledSearch: null,
  translateRequestPending: false,
  translateRequestError: S.Nothing,
  translateResult: S.Nothing,
  translateResultApplied: false,
  dateFilter: TRANSACTION_DATE_FILTER,
};

const SELECT_DATE_FILTER = "transactionSearchV2Beta/SELECT_DATE_FILTER";
const selectDateFilter = (filter) => ({
  type: SELECT_DATE_FILTER,
  payload: { filter },
});

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload, meta } = action;
  switch (type) {
    case TRANSACTION_SEARCH_PENDING:
      // A starting search retires the previous search's error alerts.
      return {
        ...state,
        searchPendingText: "Pending",
        searchRequestPending: true,
        searchRequestError: S.Nothing,
        translateRequestError: S.Nothing,
      };
    case SEARCH_REQUEST_FAILED:
      return {
        ...state,
        searchRequestPending: false,
        searchRequestError: S.Just(payload),
        lastSettledSearch: { searchId: meta?.searchId ?? null, failed: true },
      };
    case SEARCH_REQUEST_SUCCEEDED:
      return {
        ...state,
        searchRequestPending: false,
        searchRequestError: S.Nothing,
        lastSettledSearch: { searchId: meta?.searchId ?? null, failed: false },
      };
    case TRANSLATE_REQUEST:
      return {
        ...state,
        translateRequestPending: true,
        translateRequestError: S.Nothing,
        searchRequestError: S.Nothing,
      };
    case TRANSLATE_SUCCEEDED:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Nothing,
        translateResult: S.Just(payload.result),
        translateResultApplied: false,
      };
    case TRANSLATE_RESULT_APPLIED:
      return { ...state, translateResultApplied: true };
    case TRANSLATE_FAILED:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Just(payload),
        translateResult: S.Nothing,
      };
    case CLEAR_TRANSLATE:
      return {
        ...state,
        translateRequestPending: false,
        translateRequestError: S.Nothing,
        translateResult: S.Nothing,
      };
    case SET_FILTERS_VIEW: {
      // Read through the payload so a malformed dispatch can't throw and take the
      // store down. An unknown view collapses the container.
      const view = FILTERS_VIEW_VALUES.includes(payload?.view)
        ? payload.view
        : FILTERS_VIEWS.COLLAPSED;
      // Bail out when nothing changes so a repeated set cannot churn renders.
      return state.filtersView === view
        ? state
        : {
            ...state,
            filtersView: view,
          };
    }
    case SELECT_DATE_FILTER:
      return {
        ...state,
        dateFilter: payload.filter,
      };
    case OPEN_MODAL:
      return {
        ...state,
        modalCurrentlyOpen: payload.modalKey,
      };
    case CLOSE_MODAL:
      return {
        ...state,
        modalCurrentlyOpen: null,
      };
    case NEXT_PENDING_TEXT_FRAME:
      return {
        ...state,
        searchPendingText: nextPendingTextFrameMap[state.searchPendingText],
      };
    default:
      return state;
  }
};

const getState = (state) => state.transactionSearchV2Beta;
const getSelectedAgency = (state) =>
  getState(state).forms.filtersForm.agencies.rawValue;
export const getSearchPendingText = (state) => getState(state).searchPendingText;
export const getTransactionSearchCriteria = (state) =>
  state.transactionSearchV2Beta?.forms?.criteriaForm;
export const getTransactionSearchRequestPending = (state) =>
  getState(state).searchRequestPending;
export const getTransactionsSearchRequestError = (state) =>
  getState(state).searchRequestError;
export const getTranslateRequestPending = (state) =>
  getState(state).translateRequestPending;
export const getTranslateRequestError = (state) =>
  getState(state).translateRequestError;
export const getTranslateResult = (state) => getState(state).translateResult;
export const getFiltersView = (state) =>
  getState(state).filtersView ?? FILTERS_VIEWS.ALL;
export const getLastSettledSearch = (state) =>
  getState(state).lastSettledSearch ?? null;
// Read straight from the results slice instead of importing its selector:
// that module already imports from this one, and the reverse import would
// close the cycle.
export const getSearchResultsCount = (state) =>
  state.transactionSearchResultsV2Beta?.totalSearchResultsCount ?? 0;
// `transactions` is Nothing until a search lands rows, and the search epic
// resets it when the next search starts, so it marks "the count is real".
export const getSearchResultsLoaded = (state) => {
  const transactions = state.transactionSearchResultsV2Beta?.transactions;
  return !!transactions && S.isJust(transactions);
};

const isCompleteFilter = (filter) =>
  S.and(S.isJust(filter.value))(S.isJust(filter.text));

const toFilters = (fromRecord) =>
  S.map(
    S.pipe([
      S.map(fromRecord),
      S.filter(isCompleteFilter),
      S.map((filter) => ({
        value: filter.value.value,
        text: filter.text.value,
      })),
    ])
  );

const fromAgencyRecord = (agencyRec) => ({
  value: S.prop("name")(agencyRec),
  text: S.prop("name")(agencyRec),
});

const fromServiceTypeRecord = (serviceTypeRec) => ({
  value: S.prop("name")(serviceTypeRec),
  text: S.prop("description")(serviceTypeRec),
});

const fromPaymentSourceRecord = (paymentSourceRec) => ({
  value: S.prop("name")(paymentSourceRec),
  text: S.prop("name")(paymentSourceRec),
});

export const getFilterChoices = createSelector(
  getSortedServiceTypes,
  getSortedPaymentSources,
  getSortedAgencies,
  getSelectedAgency,
  (serviceTypes, paymentSources, agencies, selectedAgencyName) => {
    const agenciesRaw = S.fromMaybe([])(agencies);
    const selectedAgency = agenciesRaw.find(
      (agency) => S.fromMaybe("")(agency.name) === selectedAgencyName
    );
    const selectedAgencyPaymentSources = selectedAgency
      ? S.Just(selectedAgency.paymentSources)
      : paymentSources;
    const selectedAgencyServiceTypes = selectedAgency
      ? S.Just(selectedAgency.serviceTypes)
      : serviceTypes;

    return {
      ...FILTERS,
      [AGENCIES_FILTER]: [
        ...FILTERS[AGENCIES_FILTER],
        ...S.fromMaybe([])(toFilters(fromAgencyRecord)(agencies)),
      ],
      [SERVICE_TYPE_FILTER]: [
        ...FILTERS[SERVICE_TYPE_FILTER],
        ...S.fromMaybe([])(
          toFilters(fromServiceTypeRecord)(selectedAgencyServiceTypes)
        ),
      ],
      [PAYMENT_SOURCE_FILTER]: [
        ...FILTERS[PAYMENT_SOURCE_FILTER],
        ...S.fromMaybe([])(
          toFilters(fromPaymentSourceRecord)(selectedAgencyPaymentSources)
        ),
      ],
    };
  }
);

export const getTransactionDateFilter = (state) =>
  state.transactionSearchV2Beta.dateFilter;

export const mapStateToProps = (state) => ({
  ...getState(state),
  filterChoices: getFilterChoices(state),
  criteriaValues: getTransactionSearchCriteria(state),
  selectedDateFilter: getTransactionDateFilter(state),
  searchRequestPending: getTransactionSearchRequestPending(state),
  searchStartDate: getDatePickerStartDate(state),
  searchEndDate: getDatePickerEndDate(state),
  translateRequestPending: getTranslateRequestPending(state),
  translateRequestError: getTranslateRequestError(state),
  translateResult: getTranslateResult(state),
  filtersView: getFiltersView(state),
  lastSettledSearch: getLastSettledSearch(state),
  searchResultsLoaded: getSearchResultsLoaded(state),
  // `S.fromMaybe` requires the default to share a type with the Just value,
  // so `null` against a String tz throws sanctuary's type check. Read the
  // Maybe's `.value` directly instead.
  userTimezone: (() => {
    const tz = getUserTimezone(state);
    return S.isJust(tz) ? tz.value : null;
  })(),
});

/**
 * writeCalendarDates
 *
 * The one home for programmatic date-picker writes. `process: false` skips
 * `processCalendarDates`, which can rewrite the dates (e.g. a translate
 * result landing on a prior one); `preset: true` marks the write as
 * programmatic, like the picker's quick ranges.
 */
const writeCalendarDates = (dates) => changeCalendarDates(dates, false, true);

/**
 * resetSearchDates
 *
 * "Clear" means the default range (a year ago through today), never an
 * empty one.
 */
const resetSearchDates = () => writeCalendarDates(getDefaultSearchDates());

export const mapDispatchToProps = (dispatch) => ({
  requestSearch: (payload) => dispatch(requestSearch(payload)),
  transactionSearchPending: () => dispatch(transactionSearchPending()),
  searchRequestFailed: (payload) => dispatch(searchRequestFailed(payload)),
  searchRequestSucceeded: (payload) => dispatch(searchRequestSucceeded(payload)),
  translateRequest: (payload) => dispatch(translateRequest(payload)),
  clearTranslate: () => dispatch(clearTranslate()),
  markTranslateResultApplied: () => dispatch(markTranslateResultApplied()),
  setFiltersView: (view) => dispatch(setFiltersView(view)),
  openModal: (payload) => dispatch(openModal(payload)),
  closeModal: () => dispatch(closeModal()),
  clearFilters: () => {
    dispatch(clearFilters());
    dispatch(resetSearchDates());
  },
  selectDateFilter: (payload) => dispatch(selectDateFilter(payload)),
  // Opens the calendar on the start date, as clicking the start field does.
  openDatesCalendar: () => dispatch(openDatesModal(SEARCH_DATES.START)),
  changeCalendarDates: (dates) => dispatch(changeCalendarDates(dates, true)),
  setNlSearchDates: (dates) => dispatch(writeCalendarDates(dates)),
  resetSearchDatesToDefault: () => dispatch(resetSearchDates()),
});
export default reducer;
